import { useMemo, useRef, useCallback } from "react";
import type { CardRow, CardMeta } from "../../data/types";
import { getRowBlockType } from "../../data/types";
import BlockRenderer from "../presentations/blocks/BlockRenderer";

interface CardDiffViewProps {
  beforeBody: string;
  afterBody: string;
  beforeLabel?: string;
  afterLabel?: string;
  slugPath?: string;
}

function parseRows(body: string): CardRow[] {
  if (!body.trim()) return [];
  try {
    const parsed = JSON.parse(body);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const emptyMeta: CardMeta = {};
const noop = () => {};

function makeResolver(slugPath?: string) {
  return (src: string): string => {
    if (!src) return "";
    if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/api/")) return src;
    if (slugPath) return `/api/media/local/${slugPath}/${src}`;
    return src;
  };
}

function CardPanel({ rows, label, scrollRef, onScroll, side, resolveAsset }: {
  rows: CardRow[];
  label: string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  side: "before" | "after";
  resolveAsset: (src: string) => string;
}) {
  const accent = side === "before"
    ? { headerBg: "bg-red-50/70", dot: "bg-red-400", text: "text-red-800" }
    : { headerBg: "bg-emerald-50/70", dot: "bg-emerald-400", text: "text-emerald-800" };

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <div className={`px-3 py-2 ${accent.headerBg} flex items-center gap-1.5 border-b border-slate-200 shrink-0`}>
        <div className={`size-2 rounded-full ${accent.dot}`} />
        <span className={`text-[11px] font-semibold ${accent.text}`}>{label}</span>
      </div>
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto scrollbar-mac">
        {rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-300 text-[13px] italic py-16">
            {side === "before" ? "No previous content" : "No content"}
          </div>
        ) : (
          <div className="min-w-[70vw] p-4 space-y-4">
            {rows.map((row, idx) => {
              const blockType = getRowBlockType(row);
              return (
                <div key={idx} className="rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5 bg-white">
                  <div className="px-8 py-6">
                    <BlockRenderer row={row} blockType={blockType} cardMeta={emptyMeta} readOnly onUpdate={noop} resolveAsset={resolveAsset} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CardDiffView({ beforeBody, afterBody, beforeLabel = "Before", afterLabel = "After", slugPath }: CardDiffViewProps) {
  const beforeRows = useMemo(() => parseRows(beforeBody), [beforeBody]);
  const afterRows = useMemo(() => parseRows(afterBody), [afterBody]);
  const resolveAsset = useMemo(() => makeResolver(slugPath), [slugPath]);

  const beforeRef = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  const syncScroll = useCallback((source: "before" | "after") => {
    if (syncing.current) return;
    syncing.current = true;
    const from = source === "before" ? beforeRef.current : afterRef.current;
    const to = source === "before" ? afterRef.current : beforeRef.current;
    if (from && to) {
      const vRatio = from.scrollTop / (from.scrollHeight - from.clientHeight || 1);
      to.scrollTop = vRatio * (to.scrollHeight - to.clientHeight || 1);
      to.scrollLeft = from.scrollLeft;
    }
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);

  return (
    <div className="flex-1 flex min-h-0 overflow-hidden">
      <CardPanel rows={beforeRows} label={beforeLabel} scrollRef={beforeRef} onScroll={() => syncScroll("before")} side="before" resolveAsset={resolveAsset} />
      <div className="w-px bg-slate-200 shrink-0" />
      <CardPanel rows={afterRows} label={afterLabel} scrollRef={afterRef} onScroll={() => syncScroll("after")} side="after" resolveAsset={resolveAsset} />
    </div>
  );
}
